import { useState } from "react";
import { EmbedPage, MiniChart, SITE, Stat } from "./EmbedShell";
import { computeCityPrices, formatMoney, GRAMS_PER_TOLA, type Country } from "@/lib/gold-data";
import { computeSilverPrices, formatSilverMoney } from "@/lib/silver-data";

const COPY = {
  pakistan: {
    label: "Pakistan",
    flag: "🇵🇰",
    currency: "PKR" as const,
    unit: "per tola",
    cities: "/pakistan/cities",
    urdu: "اپنے شہر کا گولڈ ریٹ دیکھنے کے لیے یہاں کلک کریں",
    urduSilver: "اپنے شہر کا چاندی ریٹ دیکھنے کے لیے یہاں کلک کریں",
  },
  india: {
    label: "India",
    flag: "🇮🇳",
    currency: "INR" as const,
    unit: "per 10 gram",
    cities: "/india/cities",
    urdu: "अपने शहर का सोने का भाव देखने के लिए यहाँ क्लिक करें",
    urduSilver: "अपने शहर का चांदी का भाव देखने के लिए यहाँ क्लिक करें",
  },
};

export function GoldEmbed({
  country,
  spotUsdPerOz,
  fx,
  history,
  target,
}: {
  country: Country;
  spotUsdPerOz: number;
  fx: number;
  history: number[];
  target: string;
}) {
  const c = COPY[country];
  const p = computeCityPrices({ spotUsdPerOz, fxRate: fx, country, cityPremium: 1 });
  const main = country === "pakistan" ? p.perTola24 : p.per10g24;
  const m22 = country === "pakistan" ? p.perTola22 : p.per10g22;
  const m21 = country === "pakistan" ? p.perTola21 : p.per10g21;
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <EmbedPage target={target}>
      <div className="dh-card">
        <span className="dh-live">
          <span className="dh-dot" /> {c.flag} Live {c.label} Gold Rate
        </span>
        <h1 className="dh-h1">
          Gold Rate in {c.label} Today — <span data-nosnippet>{today}</span>
        </h1>
        <MiniChart points={history} label={`Gold price trend in ${c.label}`} />
        <div style={{ fontSize: 12, color: "#8b90a8" }}>6-month 24K trend ({c.currency} {c.unit})</div>
      </div>

      <div className="dh-card">
        <div className="dh-k">24K Gold {c.unit}</div>
        <div style={{ fontSize: 34, fontWeight: 900, color: "#e8c14a", lineHeight: 1.1 }}>
          {formatMoney(main, c.currency)}
        </div>
        <div style={{ fontSize: 13, color: "#9aa0b8", marginTop: 4 }}>
          1 gram 24K: <b style={{ color: "#e8c14a" }}>{formatMoney(p.perGram24, c.currency)}</b>
        </div>
        <a className="dh-cta" href={`${SITE}${target}`} target="_blank" rel="noopener">
          See full live {c.label} gold rate →
        </a>
        <a className="dh-ghost" href={`${SITE}${c.cities}`} target="_blank" rel="noopener" dir={country === "pakistan" ? "rtl" : "ltr"}>
          {country === "pakistan" ? c.urdu : c.urdu}
        </a>
      </div>

      <div className="dh-card">
        <h2 className="dh-h2">Karat-wise gold price ({c.unit})</h2>
        <div className="dh-grid">
          <Stat k="24K" v={formatMoney(main, c.currency)} />
          <Stat k="22K" v={formatMoney(m22, c.currency)} />
          <Stat k="21K" v={formatMoney(m21, c.currency)} />
        </div>
        <div className="dh-grid" style={{ marginTop: 10 }}>
          <div className="dh-card dh-lock" style={{ margin: 0 }}>
            <div className="dh-k">18K</div>
            <div className="dh-v">🔒 Locked</div>
          </div>
          <div className="dh-card dh-lock" style={{ margin: 0 }}>
            <div className="dh-k">14K</div>
            <div className="dh-v">🔒 Locked</div>
          </div>
        </div>
        <a className="dh-ghost" href={`${SITE}${target}`} target="_blank" rel="noopener">
          18K &amp; 14K rate dekhne ke liye click karein →
        </a>
      </div>

      <div className="dh-card">
        <h2 className="dh-h2">Quick gold value calculator</h2>
        <GoldMiniCalc perGram24={p.perGram24} currency={c.currency} />
        <a
          className="dh-ghost"
          href={`${SITE}/today-gold-rate-calculator-${country}`}
          target="_blank"
          rel="noopener"
        >
          Open the full {c.label} gold calculator (city + making charges) →
        </a>
      </div>

      <div className="dh-card">
        <h2 className="dh-h2">All units today</h2>
        <table>
          <thead>
            <tr>
              <th>Unit</th>
              <th>24K</th>
              <th>22K</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1 gram</td>
              <td>{formatMoney(p.perGram24, c.currency)}</td>
              <td>{formatMoney(p.perGram22, c.currency)}</td>
            </tr>
            <tr>
              <td>10 gram</td>
              <td>{formatMoney(p.per10g24, c.currency)}</td>
              <td>{formatMoney(p.per10g22, c.currency)}</td>
            </tr>
            <tr>
              <td>1 tola ({GRAMS_PER_TOLA}g)</td>
              <td>{formatMoney(p.perTola24, c.currency)}</td>
              <td>{formatMoney(p.perTola22, c.currency)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </EmbedPage>
  );
}

function GoldMiniCalc({ perGram24, currency }: { perGram24: number; currency: "PKR" | "INR" }) {
  const [w, setW] = useState("10");
  const [k, setK] = useState("22");
  const value = perGram24 * (Number(k) / 24) * (Number(w) || 0);
  return (
    <>
      <div className="dh-grid">
        <div>
          <label>Weight (grams)</label>
          <input type="number" min="0" value={w} onChange={(e) => setW(e.target.value)} />
        </div>
        <div>
          <label>Purity</label>
          <select value={k} onChange={(e) => setK(e.target.value)}>
            <option value="24">24K</option>
            <option value="22">22K</option>
            <option value="21">21K</option>
            <option value="18">18K</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 26, fontWeight: 900, color: "#e8c14a" }}>
        {formatMoney(value, currency)}
      </div>
    </>
  );
}

export function SilverEmbed({
  country,
  spotUsdPerOz,
  fx,
  target,
}: {
  country: Country;
  spotUsdPerOz: number;
  fx: number;
  target: string;
}) {
  const c = COPY[country];
  const s = computeSilverPrices({ spotUsdPerOz, fxRate: fx, country });
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  return (
    <EmbedPage target={target}>
      <div className="dh-card">
        <span className="dh-live">
          <span className="dh-dot" /> {c.flag} Live {c.label} Silver Rate
        </span>
        <h1 className="dh-h1">
          Silver Rate in {c.label} Today — <span data-nosnippet>{today}</span>
        </h1>
        <div className="dh-k">{country === "pakistan" ? "Silver per tola" : "Silver per 10 gram"}</div>
        <div style={{ fontSize: 32, fontWeight: 900, color: "#cfd6e6", lineHeight: 1.1 }}>
          {formatSilverMoney(country === "pakistan" ? s.perTola : s.per10g, c.currency)}
        </div>
        <a className="dh-cta" href={`${SITE}${target}`} target="_blank" rel="noopener">
          See full live {c.label} silver rate →
        </a>
        <a className="dh-ghost" href={`${SITE}${c.cities}`} target="_blank" rel="noopener" dir={country === "pakistan" ? "rtl" : "ltr"}>
          {c.urduSilver}
        </a>
      </div>

      <div className="dh-card">
        <h2 className="dh-h2">Silver price by unit</h2>
        <div className="dh-grid">
          <Stat k="1 gram" v={formatSilverMoney(s.perGram, c.currency)} />
          <Stat k="10 gram" v={formatSilverMoney(s.per10g, c.currency)} />
          <Stat k="1 tola" v={formatSilverMoney(s.perTola, c.currency)} />
          <Stat k="1 kg" v={formatSilverMoney(s.perKg, c.currency)} />
        </div>
      </div>

      <div className="dh-card">
        <h2 className="dh-h2">Silver value calculator</h2>
        <SilverMiniCalc perGram={s.perGram} currency={c.currency} />
        <a className="dh-ghost" href={`${SITE}/zakat-calculator`} target="_blank" rel="noopener">
          Calculate zakat on your silver &amp; gold →
        </a>
      </div>
    </EmbedPage>
  );
}

function SilverMiniCalc({ perGram, currency }: { perGram: number; currency: "PKR" | "INR" }) {
  const [w, setW] = useState("100");
  const value = perGram * (Number(w) || 0);
  return (
    <>
      <label>Weight (grams)</label>
      <input type="number" min="0" value={w} onChange={(e) => setW(e.target.value)} />
      <div style={{ marginTop: 12, fontSize: 26, fontWeight: 900, color: "#cfd6e6" }}>
        {formatSilverMoney(value, currency)}
      </div>
    </>
  );
}
